import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import db from '../lib/instant';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskCard from '../components/TaskCard';
import InviteMemberModal from '../components/InviteMemberModal';
import ProjectInvitationManager from '../components/ProjectInvitationManager';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const user = db.useUser();
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [filter, setFilter] = useState('all');

  // Query all necessary data
  const { data, error } = db.useQuery(user ? {
    projects: {},
    project_members: {},
    tasks: {},
    users: {}
  } : {});

  // Find the current project
  const project = data?.projects?.find(p => p.id === projectId);
  
  // Check if user is a member of this project
  const userMembership = data?.project_members?.find(
    pm => pm.project_id === projectId && pm.user_id === user?.id
  );

  // Get all project members
  const projectMembers = data?.project_members?.filter(pm => pm.project_id === projectId) || [];
  
  // Get all tasks for this project
  const projectTasks = data?.tasks?.filter(task => task.project_id === projectId) || [];

  // Filter tasks based on current filter
  const filteredTasks = projectTasks.filter(task => {
    switch (filter) {
      case 'pending':
        return task.status !== 'completed';
      case 'completed':
        return task.status === 'completed';
      case 'assigned_to_me':
        return task.assigned_to === user?.id;
      case 'created_by_me':
        return task.created_by === user?.id;
      default:
        return true;
    }
  });

  const getUserRole = () => {
    return userMembership?.role;
  };

  const canInviteMembers = () => {
    const role = getUserRole();
    return role === 'owner' || role === 'admin';
  };

  const handleTaskCreated = () => {
    // The query will automatically refresh due to InstantDB's reactivity
  };

  // Calculate project stats
  const getProjectStats = () => {
    const total = projectTasks.length;
    const completed = projectTasks.filter(t => t.status === 'completed').length;
    const pending = projectTasks.filter(t => t.status !== 'completed').length;
    const overdue = projectTasks.filter(t => 
      t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed'
    ).length;

    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { total, completed, pending, overdue, completionRate };
  };

  const stats = getProjectStats();

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Error loading project data. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-600">Project not found.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-2 text-blue-600 hover:text-blue-700 underline"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  if (!userMembership) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">You don't have access to this project.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-2 text-blue-600 hover:text-blue-700 underline"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Project Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/projects')}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
                {project.description && (
                  <p className="text-gray-600 mt-1">{project.description}</p>
                )}
                <div className="flex items-center space-x-4 mt-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    getUserRole() === 'owner' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                    getUserRole() === 'admin' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                    'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {getUserRole()}
                  </span>
                  <span className="text-sm text-gray-500">
                    {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {canInviteMembers() && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="button-success bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl flex items-center space-x-2 hover-lift"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  <span>Invite</span>
                </button>
              )}
              
              <button
                onClick={() => setShowCreateTaskModal(true)}
                className="button-primary bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl flex items-center space-x-2 hover-lift"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                <span>New Task</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Project Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Not Completed</p>
                <p className="text-2xl font-bold text-gray-600">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Progress</p>
                <p className="text-2xl font-bold text-purple-600">{stats.completionRate}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {stats.total > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Project Progress</h3>
              <span className="text-sm text-gray-600">{stats.completed} of {stats.total} tasks completed</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 progress-bar">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${stats.completionRate}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All Tasks', count: projectTasks.length },
              { key: 'pending', label: 'Not Completed', count: stats.pending },
              { key: 'completed', label: 'Completed', count: stats.completed },
              { key: 'assigned_to_me', label: 'Assigned to Me', count: projectTasks.filter(t => t.assigned_to === user?.id).length },
              { key: 'created_by_me', label: 'Created by Me', count: projectTasks.filter(t => t.created_by === user?.id).length }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover-lift ${
                  filter === key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white bg-opacity-20">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tasks */}
        <div className="space-y-4">
          {filteredTasks.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-500 mb-6">
                {filter === 'all' 
                  ? "This project doesn't have any tasks yet. Get started by creating the first task!" 
                  : `No tasks match the "${filter.replace('_', ' ')}" filter.`}
              </p>
              <button
                onClick={() => setShowCreateTaskModal(true)}
                className="button-primary bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl hover-lift"
              >
                Create First Task
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  users={data?.users || []}
                  currentUser={user}
                  onTaskUpdated={handleTaskCreated}
                />
              ))}
            </div>
          )}
        </div>

        {/* Project Invitation Manager */}
        <ProjectInvitationManager
          projectId={projectId}
          projectName={project.name}
          userRole={getUserRole()}
        />
      </div>

      {/* Modals */}
      <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        projectId={projectId}
        onTaskCreated={handleTaskCreated}
      />

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        projectId={projectId}
        projectName={project.name}
      />
    </div>
  );
};

export default ProjectDetail;